import { forwardRef, useState, type ChangeEvent, type FocusEvent, type ReactNode } from 'react';
import { Minus, Plus } from '../../../icons';
import { getNumberLocale } from '../../../lib/base-i18n';
import { Input, type InputProps } from './Input';

/**
 * NumberInput — 값은 오른쪽 · 등폭 숫자 · 3자리 쉼표 (02-components.md B12).
 *
 * ★★★ **보여 주는 것은 「1,250,000」이지만 밖으로 나가는 값은 숫자 1250000 이다.**
 *    금액 칸을 문자열로 주고받으면 어딘가에서 쉼표째 저장되거나 `parseInt('1,250')` 이
 *    1 이 된다 — 조용히 틀린 값이 남는 부류다. 그래서 `onValueChange` 는 `number | null` 만 준다.
 *
 * ★★ **쉼표는 «타자를 치는 동안»에는 안 넣는다.** 입력 중에 문자열을 다시 만들면 커서가
 *    맨 뒤로 튄다(숫자 가운데를 고칠 수 없게 된다). 그래서 초점이 있는 동안은 날문자열을
 *    그대로 두고, 초점을 잃을 때 다듬어 다시 그린다.
 *
 * ★ **빈 칸은 0 이 아니다** — 지운 것은 `null` 로 준다. 0 으로 바꿔 주면
 *   「아직 안 적었다」와 「0 이라고 적었다」를 구분할 수 없다.
 *
 * ★ 접근성 — 텍스트 입력 그대로 두고(읽어 주기가 가장 정확하다) 증감은 이름 붙은 버튼으로 준다.
 */
export interface NumberInputProps
  extends Omit<InputProps, 'type' | 'value' | 'onChange' | 'suffix' | 'defaultValue'> {
  value: number | null;
  onValueChange: (value: number | null) => void;
  min?: number;
  max?: number;
  /** 증감 버튼 한 번에 움직이는 양. 기본 1. */
  step?: number;
  /** 3자리 쉼표. 기본 켬. */
  thousands?: boolean;
  /** 증감 버튼을 그린다. */
  steppers?: boolean;
  /** 값 뒤에 붙는 단위 — 「원」 「시간」. 값의 일부가 아니다. */
  unit?: ReactNode;
}

const clamp = (n: number, min?: number, max?: number) => {
  let v = n;
  if (min != null && v < min) v = min;
  if (max != null && v > max) v = max;
  return v;
};

/** 쉼표·공백을 걷어내고 숫자로. 숫자가 아니면 undefined(값을 건드리지 않는다는 뜻). */
const parse = (raw: string): number | null | undefined => {
  const t = raw.replace(/[,\s]/g, '');
  if (t === '') return null;
  if (!/^-?\d*\.?\d*$/.test(t)) return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? n : undefined;
};

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(function NumberInput(
  {
    value,
    onValueChange,
    min,
    max,
    step = 1,
    thousands = true,
    steppers = false,
    unit,
    inputMode = 'numeric',
    disabled,
    readOnly,
    wrapperClassName,
    onFocus,
    onBlur,
    ...rest
  },
  ref,
) {
  /** 타자를 치는 동안의 날문자열. null 이면 «편집 중이 아니다». */
  const [draft, setDraft] = useState<string | null>(null);

  /* ⚠ 로케일을 여기 못 박으면 ja/en 사용자에게 틀린 자리표가 나간다 —
   * `getNumberLocale()`(base-i18n SSOT) 를 거친다. 레포에 이걸 막는 검사가 이미 있다
   * (`lib/i18n-locale-hardcode.test.ts` — 실제로 이 줄을 잡았다). */
  const formatted =
    value == null
      ? ''
      : thousands
        ? value.toLocaleString(getNumberLocale(), { maximumFractionDigits: 20 })
        : String(value);

  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    if (!readOnly && !disabled) setDraft(value == null ? '' : String(value));
    onFocus?.(e);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setDraft(raw);
    const parsed = parse(raw);
    if (parsed !== undefined) onValueChange(parsed);
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    const parsed = draft == null ? undefined : parse(draft);
    if (parsed === null) onValueChange(null);
    else if (parsed !== undefined) onValueChange(clamp(parsed, min, max));
    setDraft(null);
    onBlur?.(e);
  };

  const bump = (dir: 1 | -1) => {
    const base = value ?? 0;
    onValueChange(clamp(base + dir * step, min, max));
  };

  const atMax = max != null && value != null && value >= max;
  const atMin = min != null && value != null && value <= min;

  return (
    <Input
      ref={ref}
      type="text"
      inputMode={inputMode}
      value={draft ?? formatted}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      disabled={disabled}
      readOnly={readOnly}
      wrapperClassName={['wc-input--number', wrapperClassName ?? ''].filter(Boolean).join(' ')}
      suffix={
        unit != null || steppers ? (
          <>
            {unit != null && <span className="wc-input__unit">{unit}</span>}
            {steppers && (
              <span className="wc-input__steps">
                <button
                  type="button"
                  className="wc-input__btn"
                  onClick={() => bump(-1)}
                  disabled={disabled || readOnly || atMin}
                  aria-label={`${step} 줄이기`}
                >
                  <Minus aria-hidden />
                </button>
                <button
                  type="button"
                  className="wc-input__btn"
                  onClick={() => bump(1)}
                  disabled={disabled || readOnly || atMax}
                  aria-label={`${step} 늘리기`}
                >
                  <Plus aria-hidden />
                </button>
              </span>
            )}
          </>
        ) : undefined
      }
      {...rest}
    />
  );
});
